import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';

const CapitalAdvisoryPage = () => {
  const capabilities = [
    {
      title: 'Business Restructuring',
      description: 'Operational and financial restructuring strategies for businesses facing challenges or seeking optimization.',
      icon: 'ri-organization-chart',
    },
    {
      title: 'Capital Planning & Debt Advisory',
      description: 'Capital structure optimization, debt refinancing, and strategic financing solutions.',
      icon: 'ri-money-dollar-circle-line',
    },
    {
      title: 'Pro-Forma Financial Modeling',
      description: 'Detailed financial projections, scenario analysis, and business valuation services.',
      icon: 'ri-bar-chart-box-line',
    },
    {
      title: 'Lender-Ready Documentation',
      description: 'Comprehensive business plans, financial packages, and loan application support.',
      icon: 'ri-file-chart-line',
    },
    {
      title: 'Risk & Insurance Advisory',
      description: 'Risk assessment, insurance program review, and risk mitigation strategies.',
      icon: 'ri-shield-check-line',
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      <main className="pt-[90px]">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0A0A0A] to-black py-32">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 border border-copper/30 rounded-full mb-8">
                  <span className="text-xs uppercase tracking-wider text-copper">Capital Advisory</span>
                </div>
                <h1 className="text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
                  Business, Finance &{' '}
                  <span className="bg-copper-gradient bg-clip-text text-transparent">
                    Capital Advisory
                  </span>
                </h1>
                <p className="text-xl text-gray-400 leading-relaxed mb-10">
                  Strategic financial consulting for businesses requiring restructuring, capital planning, financial modeling, and lender-ready documentation to secure financing and drive growth.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-copper hover:bg-copper-light text-white px-10 py-4 rounded-full text-base font-semibold transition-all duration-300 whitespace-nowrap"
                >
                  Schedule Consultation
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="relative h-[500px] rounded-2xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/1838cafb62e7ca34f206a14ce49f312e.jpg"
                  alt="Capital Advisory"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Grid */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Our Capital Advisory Capabilities</h2>
              <p className="text-lg text-gray-400 max-w-3xl mx-auto">
                Comprehensive financial consulting services designed to optimize capital structure, secure financing, and drive sustainable business growth.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => (
                <div
                  key={index}
                  className="bg-charcoal border border-charcoal-light rounded-2xl p-8 hover:border-copper transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-copper/10 rounded-full flex items-center justify-center mb-6">
                    <i className={`${capability.icon} text-4xl text-copper`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{capability.title}</h3>
                  <p className="text-base text-gray-400 leading-relaxed">{capability.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Approach Section */}
        <section className="bg-black py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="relative h-[400px] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=business%20executives%20reviewing%20financial%20documents%20and%20strategic%20plans%20in%20professional%20boardroom%20meeting%2C%20corporate%20finance%20planning%20session%20with%20charts%20and%20presentations%20in%20sophisticated%20office%20environment&width=700&height=400&seq=capital-approach-002&orientation=landscape"
                  alt="Our Approach"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">Our Approach</h2>
                <p className="text-lg text-gray-400 leading-relaxed mb-6">
                  We combine financial expertise with strategic thinking to deliver practical solutions that improve financial performance and position businesses for success. Our methodology focuses on creating compelling financial narratives that resonate with lenders and investors.
                </p>
                <ul className="space-y-4">
                  {[
                    'Comprehensive financial analysis and assessment',
                    'Strategic capital structure optimization',
                    'Detailed financial modeling and projections',
                    'Lender and investor presentation preparation',
                    'Ongoing financial advisory and support',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-copper text-xl mt-1"></i>
                      <span className="text-base text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8 text-center">
            <h2 className="text-5xl font-bold text-white mb-6">
              Secure the Capital You Need
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Partner with MACGOC to optimize your capital structure and secure financing for growth.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-copper hover:bg-copper-light text-white px-12 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap"
            >
              Get Started
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CapitalAdvisoryPage;